import React from 'react';
import { Course } from '../types';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

interface CourseCardProps {
  course: Course;
}

const CourseCard: React.FC<CourseCardProps> = ({ course }) => {
  const { colors } = useTheme();
  
  return (
    <div 
      className="rounded-xl shadow-2xl overflow-hidden h-full flex flex-col transform transition-all duration-300 hover:scale-[1.02] hover-3d group"
      style={{
        backgroundColor: colors.background.paper,
        boxShadow: `0 10px 30px -10px ${colors.accent.mint}20`
      }}
    >
      <div className="relative h-48 overflow-hidden">
        {course.imageUrl ? (
          <img
            src={course.imageUrl}
            alt={course.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: `${colors.accent.peach}20` }}>
            <span className="text-4xl">📚</span>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent h-20"/>
        
        {/* New course badge */}
        {course.createdAt && new Date(course.createdAt).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000 && (
          <div 
            className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold"
            style={{ 
              background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
              color: colors.primary.contrastText
            }}
          >
            NEW
          </div>
        )}
      </div>
      
      <div className="p-6 flex flex-col flex-grow">
        <h3 className="text-xl font-bold mb-1" style={{ color: colors.text.primary }}>{course.title}</h3>
        <p className="text-sm font-medium mb-2" style={{ color: colors.accent.mint }}>{course.category}</p>
        <p className="text-sm leading-relaxed mb-4 line-clamp-3" style={{ color: colors.text.secondary }}>{course.description}</p>
        
        <div className="mt-auto">
          <div className="flex flex-wrap gap-2 mb-4">
            <span 
              className="text-xs px-3 py-1 rounded-full flex items-center"
              style={{ 
                backgroundColor: `${colors.primary.main}15`,
                color: colors.primary.main
              }}
            >
              👨‍🏫 {course.instructor.name}
            </span>
            <span 
              className="text-xs px-3 py-1 rounded-full flex items-center"
              style={{ 
                backgroundColor: `${colors.accent.mint}15`,
                color: colors.accent.mint
              }}
            >
              ⏱️ {course.duration} hours
            </span>
            <span 
              className="text-xs px-3 py-1 rounded-full flex items-center"
              style={{ 
                backgroundColor: `${colors.accent.mint}15`,
                color: colors.accent.mint
              }}
            >
              💰 ${course.price}
            </span>
          </div>
          
          <Link
            to={`/courses/${course._id}`}
            className="block w-full px-4 py-3 rounded-lg font-semibold transition-all duration-300 text-center hover:shadow-lg transform hover:scale-105"
            style={{
              background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
              color: colors.primary.contrastText
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `linear-gradient(135deg, ${colors.accent.mint} 0%, ${colors.primary.main} 100%)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`;
            }}
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CourseCard;
    